/**
 * Parity with mindtalk-doctors-app-react:
 *
 * **List filter** — `components/Doctors_App/consultationFIlterStrip/index.js` only exposes:
 * `all` | `draft` | `ended` | `completed` (labels Draft / Ended / Completed).
 *
 * **Detail UI labels** — `stateConfig[].visibleState` in:
 * - Psychiatrist: `Consultation.js` (consultation.consultation)
 * - Psychologist: `Psychologist/index.jsx` (clinical.psychologist.session — checkout uses `Check_Out` not `ended`)
 */

/**
 * Consultation / session state filter, in workflow order: Draft → Check In → Check Out → Completed.
 *
 * Labels match the State column rather than the legacy ConsultationFilterStrip wording:
 *  - `started` is rendered "Check In" by the column, so the filter says "Check In" too.
 *  - `ended` is rendered "Check Out", so the filter says "Check Out" (it used to say "Ended").
 *
 * Wire values are unchanged. Verified against live data — every consultation and session model
 * uses `started` for the check-in phase (psychiatrist 13, psychologist 103, allied kinds 2-13).
 */
export const MINDTALK_LIST_STATE_FILTER_OPTIONS = [
  { value: "all", label: "All states" },
  { value: "draft", label: "Draft" },
  { value: "started", label: "Check In" },
  { value: "ended", label: "Check Out" },
  { value: "completed", label: "Completed" },
];

/**
 * Evaluation forms are a different Odoo model with a different lifecycle: `draft` → `confirm`
 * → `approved`. They have no `started` / `ended` / `completed` state at all, so reusing the
 * consultation options above meant "Completed" and "Check Out" matched zero rows every time,
 * while 681 live records in `confirm` / `approved` were unreachable from the filter.
 */
export const EVALUATION_LIST_STATE_FILTER_OPTIONS = [
  { value: "all", label: "All states" },
  { value: "draft", label: "Draft" },
  { value: "confirm", label: "Confirmed" },
  { value: "approved", label: "Approved" },
];

/**
 * Mindtalk passes the same filter object to both list APIs; psychologist checkout state in Odoo is
 * `Check_Out` while psychiatrist uses `ended` for the analogous phase. Map strip value `ended` →
 * `Check_Out` for psychologist queries only.
 */
/** Session models that use `Check_Out` like `clinical.psychologist.session` (not psychiatrist `ended`). */
const PSYCHOLOGIST_LIKE_KINDS = new Set([
  "psychologist",
  "special_educator",
  "occupational_therapist",
  "speech_therapist",
  "rehabilitation_psychologist",
  "pediatric_physiotherapist",
]);

export function mindtalkStateFilterValueForApi(kind, uiState) {
  if (!uiState || uiState === "all") return uiState;
  if (PSYCHOLOGIST_LIKE_KINDS.has(kind) && uiState === "ended") {
    return "Check_Out";
  }
  return uiState;
}

/** Psychiatrist — Consultation.js stateConfig visibleState */
const PSYCHIATRIST_STATE_LABEL = {
  draft: "Draft",
  started: "Check In",
  ended: "Check Out",
  completed: "Completed",
};

/** Psychologist — Psychologist/index.jsx stateConfig visibleState */
const PSYCHOLOGIST_STATE_LABEL = {
  draft: "Draft",
  started: "Check In",
  Check_Out: "Check Out",
  completed: "Completed",
  /** Defensive: some APIs may still expose ended */
  ended: "Check Out",
};

/**
 * Table "State" column: same wording as the consultation page workflow (Draft / Check In / …).
 */
export function mindtalkConsultationStateColumnLabel(kind, backendState) {
  if (backendState == null || backendState === "") return "";
  const s = String(backendState).trim();
  if (kind === "psychiatrist") {
    return PSYCHIATRIST_STATE_LABEL[s] ?? s;
  }
  return PSYCHOLOGIST_STATE_LABEL[s] ?? s;
}

/** `psychiatrist.evaluation.form` state labels — draft / confirm / approved. */
const EVALUATION_STATE_LABEL = {
  draft: "Draft",
  confirm: "Confirmed",
  approved: "Approved",
};

/**
 * Evaluation "State" column. Previously this went through the psychiatrist map, which had no
 * entry for `confirm` / `approved` and so rendered the raw lowercase wire value.
 */
export function evaluationStateColumnLabel(backendState) {
  if (backendState == null || backendState === "") return "";
  const s = String(backendState).trim();
  return EVALUATION_STATE_LABEL[s] ?? s;
}
